import { forwardRef, type InputHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

const inputVariants = cva(
  'flex rounded-[4px] border bg-background ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'h-11 w-full border-input px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset hover:border-gray-400 disabled:hover:border-input',
        otp: 'w-15.5 h-12.5 border-gray-placeholder/20 rounded text-center text-[22px] font-medium text-black focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-100 disabled:cursor-default',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

type InputVariantProps = VariantProps<typeof inputVariants>;

export type InputVariant = NonNullable<InputVariantProps['variant']>;

export interface InputProps
  extends InputHTMLAttributes<HTMLInputElement>, Omit<InputVariantProps, 'variant'> {
  variant?: InputVariant;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(inputVariants({ variant, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

export { Input, inputVariants };
